import React from "react";

/**
 * Decorative illustration for the 404 page: the same knitted sweater on its
 * hanger, but here the accident is different — the ball of yarn has taken
 * off. On a loop it rests below the sweater, then rolls away off the right
 * edge, paying out a trail of thread behind it, while the
 * sweater watches it go with searching eyes. Inline SVG for the same reason
 * as ErrorIllustration: the error page must not depend on an asset fetch.
 *
 * All motion lives in ErrorBoundary.styles.scss, keyed on the class names
 * below, and is disabled under prefers-reduced-motion (which shows the ball
 * still at rest on its trail).
 */
const NotFoundIllustration: React.FC = () => (
  <svg
    className="error-illustration-404"
    viewBox="0 0 320 260"
    width="260"
    height="211"
    role="img"
    aria-hidden="true"
    focusable="false"
  >
    <g className="swing">
      {/* Hanger hook */}
      <path
        d="M160 18 c0 -10 14 -12 14 -2 0 6 -6 7 -6 14 v6"
        fill="none"
        stroke="#222"
        strokeWidth="4"
        strokeLinecap="round"
      />
      {/* Hanger triangle */}
      <path
        d="M160 36 L104 74 h112 Z"
        fill="none"
        stroke="#222"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      {/* Knitted sweater, whole this time — both sleeves on */}
      <path
        d="M120 84 q40 -18 80 0 l26 20 -12 16 -14 -10 v66 q-33 12 -66 0 v-66 l-14 10 -12 -16 Z"
        fill="#f5f5f5"
        stroke="#222"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      {/* Knit rows */}
      <g fill="none" stroke="#ccc" strokeWidth="2">
        <path d="M137 98 q5 5 10 0 t10 0 t10 0 t10 0 t10 0 t10 0" />
        <path d="M136 107 q5 5 10 0 t10 0 t10 0 t10 0 t10 0 t10 0" />
        <path d="M136 116 q5 5 10 0 t10 0 t10 0 t10 0 t10 0 t10 0" />
        <path d="M136 125 q5 5 10 0 t10 0 t10 0 t10 0 t10 0 t10 0" />
        <path d="M136 134 q5 5 10 0 t10 0 t10 0 t10 0 t10 0 t10 0" />
        <path d="M136 143 q5 5 10 0 t10 0 t10 0 t10 0 t10 0 t10 0" />
        <path d="M136 152 q5 5 10 0 t10 0 t10 0 t10 0 t10 0 t10 0" />
        <path d="M136 161 q5 5 10 0 t10 0 t10 0 t10 0 t10 0 t10 0" />
        <path d="M136 170 q5 5 10 0 t10 0 t10 0 t10 0 t10 0 t10 0" />
      </g>
      {/* Ribbed hem */}
      <path
        d="M134 168 q26 10 52 0"
        fill="none"
        stroke="#999"
        strokeWidth="2"
        strokeDasharray="2 4"
      />
      {/* Collar */}
      <path
        d="M138 82 q22 14 44 0"
        fill="none"
        stroke="#222"
        strokeWidth="4"
        strokeLinecap="round"
      />
      {/* Searching eyes, looking right after the runaway ball */}
      <g stroke="#222" strokeWidth="3" fill="none">
        <circle cx="148" cy="116" r="4.5" />
        <circle cx="172" cy="116" r="4.5" />
      </g>
      <circle cx="150" cy="117" r="1.8" fill="#222" stroke="none" />
      <circle cx="174" cy="117" r="1.8" fill="#222" stroke="none" />
      {/* Worried mouth */}
      <path
        d="M150 140 q10 -8 20 0"
        fill="none"
        stroke="#222"
        strokeWidth="3"
        strokeLinecap="round"
      />
      {/* The trail: thread from the hem down to the ground and off to the
          right, paid out behind the rolling ball. Path length is 203
          (getTotalLength) — the dash values in the SCSS must match. */}
      <path
        className="trail"
        d="M188 172 q4 26 12 38 q8 14 24 18 h132"
        fill="none"
        stroke="#e50010"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      {/* The runaway ball: rests, then rolls off the right edge */}
      <g className="roll-ball">
        <circle
          cx="232"
          cy="214"
          r="14"
          fill="none"
          stroke="#e50010"
          strokeWidth="4"
        />
        <path
          d="M221 208 q11 -5 22 2 m-23 7 q12 -7 23 0 m-19 6 q8 -4 16 1"
          fill="none"
          stroke="#e50010"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </g>
    </g>
  </svg>
);

export default NotFoundIllustration;
